import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ExternalLink, Loader2, Eye } from 'lucide-react';
import apiClient from '../services/apiClient';
import { toast } from 'sonner';
import { formatDateShort } from '../utils/dateFormatters';
import { tenantTodayString } from '../lib/datetime';

/**
 * Events eligible for the "preview as client" picker: today's and future days.
 *
 * `eventDate` is DATE-ONLY, so this is a calendar-day comparison in the tenant
 * zone — an event stays in the picker for the whole of its own day. The old
 * `new Date(e.eventDate) >= todayMidnight` compared a UTC-midnight instant
 * against local midnight, which dropped today's events all day long.
 */
export const filterUpcomingPreviewEvents = (events, now = new Date()) => {
  const today = tenantTodayString(now);
  return (events || []).filter((e) => {
    const day = String(e?.eventDate || '').split('T')[0];
    return day ? day >= today : true;
  });
};
export default function PreviewAsClientModal({ open, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [opening, setOpening] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/leads/automation-list');
        if (!cancelled) setEvents(res.data?.events || []);
      } catch (e) {
        toast.error('Failed to load events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = filterUpcomingPreviewEvents(events);
    list = list.slice().sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
    if (q) {
      list = list.filter((e) =>
        (e.fullName || '').toLowerCase().includes(q) ||
        (e.eventType || '').toLowerCase().includes(q) ||
        (e.eventId || '').toLowerCase().includes(q)
      );
    }
    return list.slice(0, 50);
  }, [events, query]);
  const openPortal = async (ev) => {
    setOpening(ev.eventId);
    try {
      const res = await apiClient.post(`/leads/events/${ev.eventId}/preview-client-link`);
      const token = res.data?.token;
      if (token) {
        window.open(`/client-view/${token}?preview=1`, '_blank', 'noopener,noreferrer');
        onClose();
      } else {
        toast.error('Could not generate preview link');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate preview link');
    } finally {
      setOpening(null);
    }
  };
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 pt-24 overflow-y-auto animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="preview-as-client-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden bg-[#0B1224]/90 backdrop-blur-2xl border border-white/[0.08] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)] animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <Eye className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/60">Preview</p>
              <h3 className="text-sm font-semibold text-white tracking-tight">Open a client portal</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors" data-testid="preview-as-client-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by client name, event type, or ID…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#0A0F1F]/60 border border-white/[0.08] text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 transition-colors duration-200"
              data-testid="preview-as-client-search"
            />
          </div>
          <p className="text-[11px] text-white/35 mt-2 leading-relaxed">
            Opens the client-facing portal for that event in a new tab. Safe — no emails sent, your session stays admin.
          </p>
        </div>
        <div className="px-2 pb-3 mt-3 max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/40 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-white/35 text-sm">No matching events.</div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((ev) => (
                <li key={ev.eventId}>
                  <button
                    onClick={() => openPortal(ev)}
                    disabled={opening === ev.eventId}
                    className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left border border-transparent hover:bg-white/[0.04] hover:border-white/[0.06] active:scale-[0.99] transition-all duration-150 disabled:opacity-50"
                    data-testid={`preview-as-client-pick-${ev.eventId}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white font-medium truncate">{ev.fullName || '(Unnamed)'}</p>
                      <p className="text-[11px] text-white/40 truncate">
                        {ev.eventType || '—'}
                        {ev.eventDate && <> · {formatDateShort(ev.eventDate)}</>}
                        {ev.stage && <> · {ev.stage}</>}
                      </p>
                    </div>
                    {opening === ev.eventId ? (
                      <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin shrink-0" />
                    ) : (
                      <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-[#D4AF37]/70 shrink-0 transition-colors" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
